import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Mic, Square, Loader2 } from "lucide-react";

/**
 * Bouton "🎤 Dicter" : enregistre l'audio via MediaRecorder,
 * envoie au backend pour transcription via Whisper, puis injecte le texte.
 */
export default function VoiceDictate({ onTranscript, disabled = false, compact = false }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType =
        MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        await upload(blob);
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      setError("Micro inaccessible. Autorise l'accès au microphone dans ton navigateur.");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
  };

  const upload = async (blob) => {
    if (!blob || blob.size === 0) return;
    setBusy(true);
    setError("");
    try {
      const ext = blob.type.includes("webm") ? "webm" : blob.type.includes("mp4") ? "m4a" : "wav";
      const fd = new FormData();
      fd.append("audio", blob, `note.${ext}`);
      const { data } = await api.post("/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.text) onTranscript(data.text);
      else setError("Aucun texte détecté.");
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const click = () => (recording ? stop() : start());

  if (compact) {
    const title = busy
      ? "Transcription en cours..."
      : recording
      ? "Arrêter la dictée"
      : "Dicter votre compte-rendu";
    return (
      <button
        type="button"
        onClick={click}
        disabled={disabled || busy}
        title={title}
        aria-label={title}
        data-testid="voice-dictate-compact"
        className={`relative inline-flex items-center justify-center w-10 h-10 rounded-full transition-all active:scale-90 shadow-md disabled:opacity-50 ${
          recording
            ? "bg-rose-500 hover:bg-rose-600 text-white shadow-rose-500/40 animate-pulse"
            : busy
            ? "bg-stone-300 text-white"
            : "bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/40"
        }`}
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />
        ) : recording ? (
          <Square className="w-3.5 h-3.5" fill="currentColor" strokeWidth={2.5} />
        ) : (
          <Mic className="w-4 h-4" strokeWidth={2.5} />
        )}
        {error && (
          <span className="absolute -top-9 right-0 whitespace-nowrap bg-rose-600 text-white text-[10px] font-bold px-2 py-1 rounded-md shadow-lg">
            {error}
          </span>
        )}
      </button>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={click}
        disabled={disabled || busy}
        data-testid="voice-dictate"
        className={`inline-flex items-center gap-2 font-bold px-3 py-2 rounded-full text-sm transition-all active:scale-95 shadow-md disabled:opacity-50 ${
          recording
            ? "bg-rose-500 hover:bg-rose-600 text-white shadow-rose-500/30 animate-pulse"
            : "bg-stone-900 hover:bg-stone-800 text-white shadow-stone-900/20"
        }`}
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> Transcription...
          </>
        ) : recording ? (
          <>
            <Square className="w-4 h-4" fill="currentColor" strokeWidth={2.5} /> Stop
          </>
        ) : (
          <>
            <Mic className="w-4 h-4" strokeWidth={2.5} /> Dicter
          </>
        )}
      </button>
      {error && <div className="mt-2 text-xs text-rose-600 font-bold">{error}</div>}
    </div>
  );
}
